import { lazy } from 'react';
import { authRoles } from 'app/auth';
import { Navigate } from 'react-router-dom';

import ServiceCategoryProvider from './context/ServiceCategoryContext';
import ServiceProvider from './context/ServiceContext';
import GroupServiceProvider from './context/GroupServiceContext';

import ServiceTemplateGuard from './guards/ServiceTemplateGuard';

import GroupServicesLayout from './layouts/GroupServicesLayout';
import GroupServiceDetailsLayout from './layouts/GroupServiceDetailsLayout';
import GroupServiceCategoryDetailsLayout from './layouts/GroupServiceCategoryDetailsLayout';
import PharmacyServiceDetailsLayout from './layouts/PharmacyServiceDetailsLayout';

const PharmacyServiceSettingsPage = lazy(() => import('./use-cases/PharmacyServiceSettingsPage'));
const PharmacyServiceSchedulePage = lazy(() => import('./use-cases/PharmacyServiceSchedulePage'));
const PharmacyServiceCreatePage = lazy(() => import('./use-cases/PharmacyServiceCreatePage'));
const PharmacyServicesTableView = lazy(() => import('./use-cases/PharmacyServicesTableView'));
const ConsultationRoomsTableView = lazy(() => import('./use-cases/ConsultationRoomsTableView'));
const PharmacyServiceContentPage = lazy(() => import('./use-cases/PharmacyServiceContent'));
const PharmacyServicePaymentsPage = lazy(() =>
  import('./use-cases/PharmacyServicePaymentsPage/PharmacyServicePaymentsPage')
);

const GroupServicePage = lazy(() => import('./use-cases/GroupServices'));
const GroupServiceCreatePage = lazy(() => import('./use-cases/GroupServiceCreate'));
const GroupServiceSchedulesPage = lazy(() => import('./use-cases/GroupServiceSchedules'));
const GroupServiceSettingsPage = lazy(() => import('./use-cases/GroupServiceSettings'));
const GroupServiceContentPage = lazy(() => import('./use-cases/GroupServiceContent'));
const GroupServicePaymentsPage = lazy(() => import('./use-cases/GroupServicePaymentsPage'));
const GroupServicePharmaciesPage = lazy(() => import('./use-cases/GroupServicePharmacies'));
const BlockOutDaysPage = lazy(() => import('./use-cases/BlockOutDays'));
const BlockOutDaysCreatePage = lazy(() => import('./use-cases/BlockOutDaysCreatePage'));
const BlockOutDaysEditPage = lazy(() => import('./use-cases/BlockOutDaysEditPage'));

const GroupServiceCategoriesPage = lazy(() => import('./use-cases/GroupServiceCategories'));
const GroupServiceCategoryCreatePage = lazy(() =>
  import('./use-cases/GroupServiceCategoryCreatePage')
);
const GroupServiceCategoriesSettingsPage = lazy(() =>
  import('./use-cases/GroupServiceCategoriesSettingsPage')
);
const GroupServiceCategoriesContentPage = lazy(() =>
  import('./use-cases/GroupServiceCategoriesContent')
);
const GroupQuestionnaireTableView = lazy(() => import('./use-cases/GroupQuestionnaireTableView'));
const GroupQuestionnaireBuilderPage = lazy(() =>
  import('./use-cases/GroupQuestionnaireBuilderPage')
);

const GroupServicesConfig = {
  settings: {
    layout: {
      config: {
        footer: {
          display: false,
        },
        healtheraHeader: {
          display: true,
          options: {
            organisationSelector: true,
          },
        },
      },
    },
  },
  auth: authRoles.pharmacist,
  routes: [
    {
      path: 'services',
      element: (
        <GroupServicesLayout>
          <PharmacyServicesTableView />
        </GroupServicesLayout>
      ),
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                pharmacySelector: true,
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
    {
      path: 'services/service-templates',
      element: (
        <GroupServicesLayout>
          <GroupServicePage />
        </GroupServicesLayout>
      ),
    },
    {
      path: 'services/service-templates/schedules',
      element: (
        <GroupServicesLayout>
          <GroupServiceSchedulesPage />
        </GroupServicesLayout>
      ),
    },
    {
      path: 'services/block-out-days',
      children: [
        {
          path: '',
          element: (
            <GroupServicesLayout>
              <BlockOutDaysPage />
            </GroupServicesLayout>
          ),
        },
        { path: 'create', element: <BlockOutDaysCreatePage /> },
        {
          path: ':blockOutRangeId/edit',
          element: <BlockOutDaysEditPage />,
          settings: {
            layout: {
              config: {
                healtheraHeader: {
                  display: false,
                },
              },
            },
          },
        },
      ],
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                pharmacySelector: true,
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
    {
      path: 'services/service-templates/categories',
      element: (
        <GroupServicesLayout>
          <GroupServiceCategoriesPage />
        </GroupServicesLayout>
      ),
    },
    {
      path: 'services/group-questionnaires',
      element: (
        <GroupServicesLayout>
          <GroupQuestionnaireTableView />
        </GroupServicesLayout>
      ),
    },
    {
      path: 'services/questionnaires/:questionnaireId/builder',
      element: <GroupQuestionnaireBuilderPage />,
    },
    {
      path: 'services/service-templates/categories/create',
      element: <GroupServiceCategoryCreatePage />,
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                pharmacySelector: false,
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
    {
      path: 'services/service-templates/categories/:categoryId/settings',
      element: (
        <ServiceCategoryProvider>
          <GroupServiceCategoryDetailsLayout>
            <GroupServiceCategoriesSettingsPage />
          </GroupServiceCategoryDetailsLayout>
        </ServiceCategoryProvider>
      ),
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                pharmacySelector: false,
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
    {
      path: 'services/service-templates/categories/:categoryId/content',
      element: (
        <ServiceCategoryProvider>
          <GroupServiceCategoryDetailsLayout>
            <GroupServiceCategoriesContentPage />
          </GroupServiceCategoryDetailsLayout>
        </ServiceCategoryProvider>
      ),
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                pharmacySelector: false,
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
    {
      path: 'services/service-templates/create',
      element: <GroupServiceCreatePage />,
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                pharmacySelector: false,
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
    {
      path: 'services/service-templates/:serviceTemplateId/organisations/:organisationId/settings',
      element: (
        <GroupServiceProvider>
          <GroupServiceDetailsLayout>
            <GroupServiceSettingsPage />
          </GroupServiceDetailsLayout>
        </GroupServiceProvider>
      ),
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
    {
      path: 'services/service-templates/:serviceTemplateId/organisations/:organisationId/content',
      element: (
        <ServiceTemplateGuard>
          <GroupServiceProvider>
            <GroupServiceDetailsLayout>
              <GroupServiceContentPage />
            </GroupServiceDetailsLayout>
          </GroupServiceProvider>
        </ServiceTemplateGuard>
      ),
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
    {
      path: 'services/service-templates/:serviceTemplateId/organisations/:organisationId/payments',
      element: (
        <ServiceTemplateGuard>
          <GroupServiceProvider>
            <GroupServiceDetailsLayout>
              <GroupServicePaymentsPage />
            </GroupServiceDetailsLayout>
          </GroupServiceProvider>
        </ServiceTemplateGuard>
      ),
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
    {
      path: 'services/service-templates/:serviceTemplateId/organisations/:organisationId/pharmacies',
      element: (
        <ServiceTemplateGuard>
          <GroupServiceProvider>
            <GroupServiceDetailsLayout>
              <GroupServicePharmaciesPage />
            </GroupServiceDetailsLayout>
          </GroupServiceProvider>
        </ServiceTemplateGuard>
      ),
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
    {
      path: 'services/consultation-rooms',
      element: (
        <GroupServicesLayout>
          <ConsultationRoomsTableView />
        </GroupServicesLayout>
      ),
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                pharmacySelector: true,
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
    {
      path: 'services/create',
      element: <PharmacyServiceCreatePage />,
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                pharmacySelector: false,
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
    {
      path: 'services/:serviceId',
      element: <Navigate to="settings" replace />,
    },
    {
      path: 'services/:serviceId/settings',
      element: (
        <ServiceProvider>
          <PharmacyServiceDetailsLayout>
            <PharmacyServiceSettingsPage />
          </PharmacyServiceDetailsLayout>
        </ServiceProvider>
      ),
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                pharmacySelector: true,
                pharmacySelectorHidden: true,
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
    {
      path: 'services/:serviceId/content',
      element: (
        <ServiceProvider>
          <PharmacyServiceDetailsLayout>
            <PharmacyServiceContentPage />
          </PharmacyServiceDetailsLayout>
        </ServiceProvider>
      ),
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                pharmacySelector: true,
                pharmacySelectorHidden: true,
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
    {
      path: 'services/:serviceId/schedule',
      element: (
        <ServiceProvider>
          <PharmacyServiceDetailsLayout>
            <PharmacyServiceSchedulePage />
          </PharmacyServiceDetailsLayout>
        </ServiceProvider>
      ),
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                pharmacySelector: true,
                pharmacySelectorHidden: true,
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
    {
      path: 'services/:serviceId/payments',
      element: (
        <ServiceProvider>
          <PharmacyServiceDetailsLayout>
            <PharmacyServicePaymentsPage />
          </PharmacyServiceDetailsLayout>
        </ServiceProvider>
      ),
      settings: {
        layout: {
          config: {
            healtheraHeader: {
              display: true,
              options: {
                pharmacySelector: true,
                pharmacySelectorHidden: true,
                organisationSelector: false,
              },
            },
          },
        },
      },
    },
  ],
};

export default GroupServicesConfig;
